import { requestStructuredOutput } from "@/lib/ai/structured-outputs";
import { getSectionTypes } from "@/lib/singularity/singularity";
import { globalStore, singularityStore } from "@/store/store";
import { PageDescription, PageDescriptionV } from "@/types/singularity";
import { getTimeElapsedFunction } from "../generate-homepage";
import {
  getChatMessagesToRequestSectionPopulation,
  getTreeFromSectionDescriptions,
  populatePhantomSectionWithAiData
} from "../sections";
import { addOrUpdatePageInWP } from "@/lib/singularity/wp-page-manager/pages";
import { BatchResponse, callAIBatch } from "../api/api";
import { Tree } from "@/types/tree";
import defensiveCopy from "@/util/defensive-copy";
import { createTreeFromExportedTree, findNode } from "@/lib/tree/tree";
import { cleanJson } from "@/lib/ai/api";
import { getFieldsToComplete } from "@/lib/ai/ai";
import { replaceImagesInSectionWithAiMutating } from "../images/main";
import {
  addHeaderAndFooterNodesToTree,
  removeHeaderAndFooterNodesFromTree
} from "../headers-footers/headers-footers";
import { setHeaderOverlayBehaviorForPageTree } from "../headers-footers/overlay-headers";
import { getBlockFromWordPress } from "@/lib/global-blocks/global-blocks";
import { fold } from "fp-ts/lib/Either";
import { BREAKDANCE_BLOCK_POST_TYPE } from "@/lib/constants/post-types";
import { GlobalBlock } from "@/types/global-blocks";
import { PhantomPage } from "../phantom-page-holder";

export async function generatePageInTheBackground(
  pageTitle: string,
  idOfPageInWP: number,
  abortController: AbortController,
  description?: string
) {
  const otherPagesOnSite = singularityStore.pagesInWP.map(page => page.title);
  var timeElapsed = getTimeElapsedFunction();

  const pageDescription = await generatePageDescriptionWithLlm(
    pageTitle,
    otherPagesOnSite,
    abortController,
    description
  );
  console.log("generatePageDescriptionWithLlm done", timeElapsed());

  if (!pageDescription) return;

  let pageTree = addHeaderAndFooterNodesToTree(
    getTreeFromSectionDescriptions(pageDescription.sections)
  );
  pageTree = setHeaderOverlayBehaviorForPageTree(pageTree);
  console.log("getTreeFromSectionDescriptions done", pageTree, timeElapsed());

  /**
   * save the page to WP before writing the content, just in case the user leaves the process
   * and then if the user comes back later and opens up the page, it will write the content
   */
  var updatedPage = await addOrUpdatePageInWP({
    id: idOfPageInWP,
    title: pageTitle,
    postType: "page",
    tree: pageTree,
    singularityMeta: {
      singularityInitialSections: pageDescription.sections,
      initialSectionsAlreadyPopulated: false
    }
  });
  console.log("addOrUpdatePageInWP done", updatedPage, timeElapsed());

  const pageTreeToRewrite = removeHeaderAndFooterNodesFromTree(pageTree);

  /**
   * batch rewrite the sections with the LLM and populate the tree with the response from the llm
   */
  const batchRewriteRequests = getBatchRequestsForPage({
    title: pageTitle,
    tree: pageTreeToRewrite
  });
  console.log(
    "getBatchRequestsForPage done",
    batchRewriteRequests,
    timeElapsed()
  );

  const batchResponses = await callAIBatch(batchRewriteRequests.requests, abortController);
  console.log("callAIBatch done", batchResponses, timeElapsed());

  const populatedTree = populateTreeFromBatchResponse(
    pageTreeToRewrite,
    batchResponses
  );
  console.log(
    "populateTreeFromBatchResponse done",
    populatedTree,
    timeElapsed()
  );

  const treeWithImages = await populateTreeWithImages(populatedTree, abortController);

  let populatedTreeWithHeaderAndFooter =
    addHeaderAndFooterNodesToTree(treeWithImages);
  populatedTreeWithHeaderAndFooter = setHeaderOverlayBehaviorForPageTree(populatedTreeWithHeaderAndFooter);

  /**
   * update the rewritten page in WP
   */
  updatedPage = await addOrUpdatePageInWP({
    id: idOfPageInWP,
    title: pageTitle,
    postType: "page",
    tree: populatedTreeWithHeaderAndFooter,
    singularityMeta: {
      singularityInitialSections: pageDescription.sections,
      initialSectionsAlreadyPopulated: true
    }
  });
  if (!updatedPage) return;
  console.log("addOrUpdatePageInWP done", updatedPage, timeElapsed());

  return updatedPage;
}

export async function generatePageDescriptionWithLlm(
  titleOfPageToGenerate: string,
  titlesOfOtherPagesOnSite: string[],
  abortController: AbortController,
  description?: string
): Promise<PageDescription | undefined> {
  const websiteName = globalStore.aiSettings.prompt.businessName;
  const websiteDescription = globalStore.aiSettings.prompt.businessDescription;

  const sectionTypesString = getSectionTypes()
    .map(sectionType => `'${sectionType}'`)
    .join(", ");

  var prompt = ``;

  const pagesOnSiteAsString = titlesOfOtherPagesOnSite
    .map(page => `- ${page}`)
    .join("\n");

  const addtlInfoAboutPage = description
    ? `\nAdditional information about the page:\n${description}`
    : "";

  prompt = `We have a website with the following pages:
            ${pagesOnSiteAsString}
    
          Website name: ${websiteName}
          Website description: ${websiteDescription}

          You will generate the list of sections for the page page titled "${titleOfPageToGenerate}"
          ${addtlInfoAboutPage}

          The page should have an appropriate number of sections. Maybe 8 for a homepage, 3 or 4 for a services page, and just one or two for the contact page. Feel free to deviate from this if you think it makes sense.

          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.
          `;

  const result = await requestStructuredOutput(prompt, PageDescriptionV, abortController, true);

  return result;
}

export function populateTreeFromBatchResponse(
  tree: Tree,
  sectionRewriteResponses: BatchResponse[]
) {
  const populatedTree = defensiveCopy(tree);

  sectionRewriteResponses.forEach(sectionRewriteResponse => {
    const sectionToOperateOn = findNode(
      populatedTree,
      Number(sectionRewriteResponse.id)
    );
    if (!sectionToOperateOn) return;
    populatePhantomSectionWithAiData(
      sectionToOperateOn,
      cleanJson(sectionRewriteResponse.content ?? ""),
      getFieldsToComplete(sectionToOperateOn),
      populatedTree
    );
  });

  return populatedTree;
}

export async function populateTreeWithImages(tree: Tree, abortController: AbortController): Promise<Tree> {
  console.log("populateTreeWithImages started");
  const populatedTree = defensiveCopy(tree);

  const promises = populatedTree.root.children.map(section => {
    return replaceImagesInSectionWithAiMutating(section, abortController);
  });

  console.warn("awaiting promise.all");
  await Promise.all(promises);
  console.warn("promise.all done");

  console.log("populateTreeWithImages done");

  return populatedTree;
}

export async function rewriteGlobalBlockInTheBackground(blockId: number, abortController: AbortController) {
  var timeElapsed = getTimeElapsedFunction();

  const globalBlockResult = await getBlockFromWordPress(blockId);

  const globalBlock = fold(
    () => null as GlobalBlock | null,
    (block: GlobalBlock) => block
  )(globalBlockResult);

  if (!globalBlock || !globalBlock.tree) {
    console.error("Failed to load existing global block tree");
    return;
  }

  const tree = createTreeFromExportedTree(globalBlock.tree);

  console.log("Loaded existing global block tree", timeElapsed());

  const batchRewriteRequests = getBatchRequestsForPage({
    title: globalBlock.label,
    tree: tree
  });
  console.log(
    "getBatchRequestsForPage done",
    batchRewriteRequests,
    timeElapsed()
  );

  const batchResponses = await callAIBatch(batchRewriteRequests.requests, abortController);
  console.log("callAIBatch done", batchResponses, timeElapsed());

  const populatedTree = populateTreeFromBatchResponse(tree, batchResponses);
  console.log(
    "populateTreeFromBatchResponse done",
    populatedTree,
    timeElapsed()
  );

  const treeWithImages = await populateTreeWithImages(populatedTree, abortController);

  const updatedGlobalBlock = await addOrUpdatePageInWP({
    id: blockId,
    title: globalBlock.label,
    postType: BREAKDANCE_BLOCK_POST_TYPE,
    tree: treeWithImages
  });

  if (!updatedGlobalBlock) return;
  console.log(
    "addOrUpdatePageInWP - updatedGlobalBlock - done",
    updatedGlobalBlock,
    timeElapsed()
  );

  return updatedGlobalBlock;
}

function getBatchRequestsForPage(page: PhantomPage) {
  return {
    title: page.title,
    requests: page.tree.root.children.map(section => {
      return {
        id: String(section.id),
        messages: getChatMessagesToRequestSectionPopulation(section)
      };
    })
  };
}
